import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert
} from "@mui/material";

import { getProjects, getProject, updateProject, getEmployeeCapacity } from "../../services/projectService";
import ResourceCalendar from "./ResourceCalendar";
import CapacitySlider from "../../ui/CapacitySlider";
import AppSelect from "../../ui/AppSelect";
import AppButton from "../../ui/AppButton";

function AssignProjectDialog({ open, employee, onClose, onAssigned }) {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [capacityPercent, setCapacityPercent] = useState(0);
  const [availableCapacity, setAvailableCapacity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedProjectId("");
    setCapacityPercent(0);
    setAvailableCapacity(null);
    setError("");

    const loadProjects = async () => {
      try {
        setLoadingProjects(true);
        const data = await getProjects();
        setProjects(data);
      } catch (err) {
        console.error("Error loading projects:", err);
        setError("Failed to load projects");
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [open]);

  useEffect(() => {
    const loadAvailableCapacity = async () => {
      if (!selectedProjectId || !employee) {
        setAvailableCapacity(null);
        return;
      }

      try {
        const project = projects.find((p) => p.project_id === selectedProjectId);

        const capacity = await getEmployeeCapacity(
          employee.employee_id,
          selectedProjectId,
          project?.start_date || null,
          project?.end_date || null
        );

        setAvailableCapacity(capacity.available_capacity_percent);
      } catch (err) {
        console.error("Error loading employee capacity:", err);
        setAvailableCapacity(null);
      }
    };

    loadAvailableCapacity();
  }, [selectedProjectId, employee, projects]);

  const handleAssign = async () => {
    if (!selectedProjectId || !employee) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const project = await getProject(selectedProjectId);

      const mergedAssignedTo = [
        ...project.assigned_to.filter((a) => a.employee_id !== employee.employee_id),
        { employee_id: employee.employee_id, capacity_percent: capacityPercent }
      ];

      await updateProject(selectedProjectId, {
        project_name: project.project_name,
        description: project.description,
        start_date: project.start_date,
        end_date: project.end_date,
        assigned_to: mergedAssignedTo
      });

      onAssigned?.();
      onClose();
    } catch (err) {
      console.error("Error assigning project:", err);
      setError(err?.response?.data?.detail || "Failed to assign project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: "90%" } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Assign Project{employee ? ` to ${employee.employee_name}` : ""}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
          <AppSelect
            label="Project"
            options={projects.map((project) => ({ value: project.project_id, label: project.project_name }))}
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            isDisabled={loadingProjects}
            isLoading={loadingProjects}
            placeholder="Select project"
          />

          <Box>
            <CapacitySlider
              name={employee ? employee.employee_name : "Employee"}
              value={capacityPercent}
              onChange={(e, value) => setCapacityPercent(value)}
              max={100}
            />

            {availableCapacity !== null && (
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Available capacity for this employee: {availableCapacity}%
              </Typography>
            )}
          </Box>

          {employee && (
            <Box>
              <Typography sx={{ fontSize: 13, color: "#475569", fontWeight: 600, mb: 1.5 }}>
                Current Capacity
              </Typography>

              <Box sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
                <ResourceCalendar employeeId={employee.employee_id} showProjectsTable={false} />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <AppButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </AppButton>

        <AppButton
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedProjectId}
          loading={saving}
        >
          Assign
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

export default AssignProjectDialog;
